import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ConceptsService } from '@core/api/generated/api/concepts.service';

/**
 * Guards the `concepts/:conceptId` route: the concept must exist in the
 * currently selected project/branch. If it does not exist (e.g. because the
 * user switched to a branch where the concept was not yet created, or the
 * concept ID is simply invalid), the guard redirects to the Concepts
 * overview (`concepts`) instead of letting the detail page render an error.
 *
 * `projectId` and `branchName` are parameters of the parent route
 * (`projects/:projectId/:branchName`). Because `paramsInheritanceStrategy`
 * is set to `'always'` (see app.config.ts), `route.paramMap` already
 * contains the inherited parent params, so no manual `route.parent`
 * traversal is necessary.
 *
 * This guard should be applied as a `canActivate` guard on the
 * `concepts/:conceptId` route, complementing `projectExistsGuard` and
 * `branchExistsGuard`, which already validated `projectId`/`branchName`.
 *
 * Existence is checked via the summary endpoint, not the detail endpoint (AMONE-191).
 * Both return 404 for an unknown concept, but the detail endpoint additionally renders
 * every diagram in the concept body. Using it here meant every navigation to a concept
 * paid for that rendering twice: once in the guard, whose response is discarded, and
 * once in the detail component that loads the concept afterwards.
 */
export const conceptExistsGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const conceptsService = inject(ConceptsService);

  const projectId = route.paramMap.get('projectId');
  const branchName = route.queryParamMap.get('branchName');
  const conceptId = route.paramMap.get('conceptId');

  // If any required param is missing, let other guards/logic handle it
  // (should practically not happen, since parent guards already ensure
  // projectId/branchName, and the route itself declares :conceptId).
  if (!projectId || !branchName || !conceptId) {
    return true;
  }

  return conceptsService.getConceptSummary(projectId, branchName, conceptId).pipe(
    map(() => true),
    catchError(() =>
      of(
        router.createUrlTree(['/projects', projectId, 'concepts'], { queryParams: { branchName } })
      )
    )
  );
};
